import {
  Button,
  Checkbox,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { RosterMember } from "../../common/Dispatch";
import { formatDateString } from "../../common/FormatDateString";
import "../../components/vesselRosterManagement/RosterStatusModal.scss";
import { JobOrder, JobRating, JobRatingLabel } from "../../common/JobOrder";
import { Gender, GenderSymbol, MarinerUnion } from "../../common/MarinerProfile";
import EmailModal from "../email/EmailModal";
import { EmailType } from "../../common/EmailDetails";
import { toast } from "react-toastify";
import { Vessel } from "../../common/Vessel";
import { Email } from "@mui/icons-material";

interface RosterStatusModalProps {
  rosterStatusData: RosterMember[];
  vessel: Vessel;
  children: Element | any;
}

type TravelEmailData = {
  passenger: string;
  gender: string;
  dob: string;
  twicTsaNum: string;
  from: string;
  to: string;
  date: string;
  time: string;
  hotelOrRental: string;
  pickUpDate: string;
  dropOffDate: string;
  phone: string;
}

type RosterStatus = {
  id: string;
  date: string;
  offsignerName: string;
  onsignerName: string;
  port: string;
  rating: JobRating;
  offsignerAgentUnion?: string;
  jobOrderId?: number;
  offsignerEmailData: TravelEmailData;
  onsignerEmailData: TravelEmailData;
};

const RosterStatusModal = (props: RosterStatusModalProps) => {
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pageData, setPageData] = useState<RosterStatus[]>([]);
  const [emailDataMap, setEmailDataMap] = useState<Map<string, TravelEmailData>>(new Map());
  const [vessel, setVessel] = useState<Vessel>();

  useEffect(() => {
    const { rosterStatusData, vessel } = props;
    let statuses: RosterStatus[] = [];
    rosterStatusData
      .filter(
        (item) =>
          !!item.jobOrder?.id &&
          new Date(new Date(item.jobOrder?.fillByDate).toDateString()) >=
            new Date(new Date().toDateString()) &&
          (!!item.jobOrder.offsigner?.id || !!item.jobOrder.onsigner?.id)
      )
      .sort((a: RosterMember, b: RosterMember) =>
        new Date(a.jobOrder?.fillByDate) > new Date(b.jobOrder?.fillByDate)
          ? 1
          : -1
      )
      .map((item) => {
        const status: RosterStatus = {
          id: (item.id as number) + "_status",
          offsignerName: !!item.jobOrder.offsigner?.id
            ? item.jobOrder.offsigner?.firstName +
              " " +
              item.jobOrder.offsigner?.middleName +
              " " +
              item.jobOrder.offsigner?.lastName
            : "",
          onsignerName: !!item.jobOrder.onsigner?.id
            ? item.jobOrder.onsigner?.firstName +
              " " +
              item.jobOrder.onsigner?.middleName +
              " " +
              item.jobOrder.onsigner?.lastName
            : "",
          date: item.jobOrder.fillByDate,
          port: item.jobOrder.port as string,
          rating: item.jobOrder.rating,
          offsignerAgentUnion: item.jobOrder.offsigner?.agentUnion,
          jobOrderId: item.jobOrder.id,
          offsignerEmailData: populateEmailData(item.jobOrder, true),
          onsignerEmailData: populateEmailData(item.jobOrder, false)
        };
        statuses.push(status);
        return item;
      });

    setPageData(statuses);
    setVessel(vessel);
  }, [props]);

  const populateEmailData = (jobOrder: JobOrder, offsign: boolean) => {
    const profile = offsign ? jobOrder?.offsigner : jobOrder?.onsigner;
    return {
      vesselName: jobOrder.vessel?.name as string,
      passenger:
        profile?.firstName +
        " " +
        profile?.middleName +
        " " +
        profile?.lastName,
      gender:
        GenderSymbol[
        profile?.gender as Gender
        ],
      dob:
        profile?.dateOfBirth ===
          undefined ||
          profile?.dateOfBirth === null
          ? "null"
          : formatDateString(
            profile?.dateOfBirth
          ),
      twicTsaNum: profile
        ?.twicTsaNum as string,
      from: offsign ? jobOrder?.offsignerLeavingFrom as string : jobOrder?.onsignerLeavingFrom as string,
      to: offsign ? jobOrder?.offsignerDestination as string : jobOrder?.onsignerDestination as string,
      date:
        jobOrder?.fillByDate === undefined ||
          jobOrder?.fillByDate === null
          ? "null"
          : formatDateString(jobOrder?.fillByDate),
      time: offsign ? jobOrder?.offsignerTravelTimestamp as string : jobOrder?.onsignerTravelTimestamp as string,
      hotelOrRental:
        offsign ? jobOrder?.offsignerAccomodation as string : jobOrder?.onsignerAccomodation as string,
      pickUpDate:
        offsign ? 
        jobOrder?.offsignerPickupDate ===
          undefined ||
          jobOrder?.offsignerPickupDate === null
          ? "null"
          : formatDateString(
            jobOrder?.offsignerPickupDate
          ) :
        jobOrder?.onsignerPickupDate ===
          undefined ||
          jobOrder?.onsignerPickupDate === null
          ? "null"
          : formatDateString(
            jobOrder?.onsignerPickupDate
          ),
      dropOffDate:
        offsign ?
        jobOrder?.offsignerDropoffDate ===
          undefined ||
          jobOrder?.offsignerDropoffDate === null
          ? "null"
          : formatDateString(
            jobOrder?.offsignerDropoffDate
          ) :
        jobOrder?.onsignerDropoffDate ===
          undefined ||
          jobOrder?.onsignerDropoffDate === null
          ? "null"
          : formatDateString(
            jobOrder?.onsignerDropoffDate
          ),
      phone: profile?.phone as string,
    }
  }

  const handleCheck = (checked: boolean, row: RosterStatus, offsign: boolean) => {
    const key = `${row.jobOrderId}-${offsign ? 'offsign' : 'onsign'}`;
    setEmailDataMap(map => {
      const newMap = new Map(map);
      if(checked){
        newMap.set(key, offsign ? row.offsignerEmailData : row.onsignerEmailData);
      } else {
        newMap.delete(key);
      }
      return newMap;
    })
  }

  const handleEmailCallBack = () => {
    setEmailOpen(false);
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  return (
    <div>
      <span onClick={() => setOpen(true)}>{props.children}</span>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        PaperProps={{
          sx: emailOpen ? { display: "none" } : {}
        }}
        className="email-modal"
      >
        <div className="modal-header">
          <h2>Offsign List</h2>
          <EmailModal
            key={EmailType.EMAIL_TO_TRAVEL_AGENT + "_multi"}
            vesselId={vessel?.id}
            emailDataList={Array.from(emailDataMap.values())}
            jobOrderActions={Array.from(emailDataMap.keys()).map(k => {
              const [id, type] = k.split('-');
              return {
                jobOrderId: parseInt(id),
                action: type === 'offsign' ? 'Sent to Travel Agent (Offsigner)' : 'Sent to Travel Agent (Onsigner)'
              };
            })}
            email={undefined}
            emailType={EmailType.EMAIL_TO_TRAVEL_AGENT_MULTI}
            parentCallback={handleEmailCallBack}
            closeCallback={() => setEmailOpen(false)}
          >
            <Button className="blue-link-button" disabled={emailDataMap.size === 0} onClick={() => setEmailOpen(true)}>
              <Email />
              Email Travel Agent
            </Button>
          </EmailModal>
          <Button onClick={() => setOpen(false)} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <TableContainer className="upcoming-table">
          <Table sx={{ minWidth: 750 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell className="tr-border-off">Rating</TableCell>
                <TableCell>Offsigner</TableCell>
                <TableCell>Crew Change Needed</TableCell>
                <TableCell>Port Relief Requested</TableCell>
                <TableCell className="tr-mariner">Onsigner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Tooltip
                      className="tr-border-off"
                      title={JobRatingLabel[row.rating as JobRating]}
                      placement="right"
                    >
                      <span>{row.rating.substring(1)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Checkbox
                      checked={emailDataMap.has(`${row.jobOrderId}-offsign`)}
                      onChange={(e) => handleCheck(e.target.checked, row, true)}
                      size="small"
                    />
                    {row.offsignerName}
                    {row.offsignerAgentUnion === MarinerUnion.NON_UNION && (
                      <div className="extra-flag">
                        <Tooltip title={"Non-Union"} placement="top">
                          <span>NU</span>
                        </Tooltip>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="tr-date">
                    {formatDateString(row.date)}
                  </TableCell>
                  <TableCell>{row.port}</TableCell>
                  <TableCell>
                    {row.onsignerName && <>
                      <Checkbox
                        checked={emailDataMap.has(`${row.jobOrderId}-onsign`)}
                        onChange={(e) => handleCheck(e.target.checked, row, false)}
                        size="small"
                      />
                      {row.onsignerName}
                    </>}
                  </TableCell>
                </TableRow>
              ))}
              {(!pageData || pageData.length === 0) && (
                <TableRow key={"no_changes"}>
                  <TableCell>No mariners Departing / Onboarding.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Dialog>
    </div>
  );
};

export default RosterStatusModal;
